import type { FormInstance } from 'antd';
import { Divider, Form } from 'antd';
import classNames from 'classnames';
import type { FieldData } from 'rc-field-form/es/interface';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useSaveExperiences } from 'app/hooks/data/core/extranet/inventory/experiences/useExperiences';
import { mapToExperienceResponse } from 'app/hooks/data/core/extranet/inventory/mapping/experience';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';
import { isPackVisibleInExtranet } from 'app/utils/extranet';

import { inventoryStore } from '../../../InventoryStore';
import type {
  InventoryExperiences,
  InventoryPacks,
  InventoryRooms,
  ModifiedData,
} from '../../../InventoryStore';
import { TimeslotsModal } from '../../../_modals/TimeslotsModal/TimeslotsModal';
import {
  useExperienceInventoryState,
  useInventoryState,
  useTabInventoryActions,
} from '../../../hooks';
import {
  getChangedFormFields,
  getModifiedValues,
  mergeInventoryObjects,
  retrieveDataFromNamePath,
  retrieveFormValues,
} from '../../../utils';
import { updateExperienceStockForDate } from '../../../utils/inventoryUpdates';
import { EmptyState } from '../../EmptyState/EmptyState';
import { ExperienceTabSkeleton } from '../../Skeletons/ExperienceTabSkeleton/ExperienceTabSkeleton';
import { formatExperienceName, isEnabledByMessage } from '../tabViewUtils';

import { ExperienceConfigurationDrawer } from './ExperienceConfigurationDrawer/ExperienceConfigurationDrawer';
import styles from './ExperiencesTab.module.scss';
import { ValidationExperienceManualEditModal } from './ValidationExperienceManualEditModal/ValidationExperienceManualEditModal';
import { ExperienceTableItem } from './_components/ExperienceTableItem';
import { useFilteredExperiences } from './hooks/useFilteredExperiences';

type ManualEditValidationPayload = {
  modifiedValues: ModifiedData;
  experienceNames: string[];
  packNames: string[];
};

const getMustHaveImpact = (
  modifiedValues: ModifiedData,
  packsList: ExtranetHotelPack[],
  experienceNameById: Map<number, string>
): { experienceNames: string[]; packNames: string[] } => {
  const closedExperienceIds = new Set<number>();

  Object.values(modifiedValues).forEach((entriesByItem) => {
    Object.entries(entriesByItem).forEach(([itemId, changedFields]) => {
      const remainingStock = (
        changedFields as { remainingStock?: number | null }
      ).remainingStock;

      if (remainingStock === 0 || remainingStock === null) {
        closedExperienceIds.add(Number(itemId));
      }
    });
  });

  if (closedExperienceIds.size === 0) {
    return { experienceNames: [], packNames: [] };
  }

  const impactedPackNames = new Set<string>();
  const impactedExperienceNames = new Set<string>();

  packsList.forEach((pack) => {
    if (!isPackVisibleInExtranet(pack.status)) {
      return;
    }

    pack.packExperiences.forEach((pe) => {
      if (
        pe.isIncluded &&
        !pe.isOptional &&
        closedExperienceIds.has(pe.experienceId)
      ) {
        impactedPackNames.add(pack.nameForHotel || pack.nameForUser);

        const expName = experienceNameById.get(pe.experienceId);

        if (expName) {
          impactedExperienceNames.add(expName);
        }
      }
    });
  });

  return {
    experienceNames: Array.from(impactedExperienceNames),
    packNames: Array.from(impactedPackNames),
  };
};

export const ExperiencesTab: React.FC = () => {
  const { hotelInfo } = useContext(HotelContext);
  const { packsList } = useContext(ModernHotelContext);
  const [form] = Form.useForm<InventoryExperiences>();
  const {
    sortedExperiences,
    sortedExperiencesIsAlwaysIncludedWithNight,
    experiencesCategories,
    isFetching,
  } = useFilteredExperiences();

  const setIsEditMode = useStore(
    inventoryStore,
    (state) => state.setIsEditMode
  );

  const [manualEditValidationPayload, setManualEditValidationPayload] =
    useState<ManualEditValidationPayload | null>(null);
  const [isManualEditSaving, setIsManualEditSaving] = useState(false);

  const { timePeriod } = useInventoryState();

  const { mutateAsync: saveExperienceInventory } = useSaveExperiences(
    hotelInfo?.id,
    timePeriod
  );

  const {
    inventoryExperiences,
    inventory,
    dependencies,
    rawInventory,
    modifiedInventory,
    timePeriodStore,
  } = useExperienceInventoryState();

  const { setForm, setModifiedInventory } = useTabInventoryActions();

  useEffect(() => {
    setForm(
      form as FormInstance<
        InventoryExperiences | InventoryRooms | InventoryPacks
      >
    );
  }, [form, setForm]);

  useLayoutEffect(() => {
    const mergedInventory = mergeInventoryObjects(
      inventory ?? {},
      timePeriodStore
    );

    form.setFieldsValue(
      retrieveFormValues(mergedInventory).inventoryExperiences
    );

    //We don't want this to re-run on every form change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy directive, needs investigation
  }, [inventoryExperiences]);

  const saveExperienceInventoryFromModified = async (
    modifiedValues: ModifiedData
  ) => {
    await saveExperienceInventory(mapToExperienceResponse(modifiedValues));
  };

  const onFinish = async (values: InventoryExperiences) => {
    const { inventoryExperiences: expTimePeriodStore } =
      retrieveFormValues(timePeriodStore);

    const modifiedValues = getModifiedValues(
      { ...values, ...expTimePeriodStore },
      inventoryExperiences
    );

    const experienceNameById = new Map<number, string>(
      [...sortedExperiences, ...sortedExperiencesIsAlwaysIncludedWithNight].map(
        (exp) => {
          const categoryName = experiencesCategories?.find(
            (cat) => cat.id === exp.categoryId
          )?.name;

          return [exp.id, formatExperienceName(exp, categoryName)];
        }
      )
    );

    const { experienceNames, packNames } = getMustHaveImpact(
      modifiedValues,
      packsList,
      experienceNameById
    );

    if (packNames.length > 0) {
      setManualEditValidationPayload({
        modifiedValues,
        experienceNames,
        packNames,
      });

      return;
    }

    await saveExperienceInventoryFromModified(modifiedValues);
  };

  const handleCancelManualEditValidation = () => {
    setManualEditValidationPayload(null);
    setIsEditMode(true);
  };

  const handleSaveManualEditValidation = async () => {
    if (!manualEditValidationPayload) {
      return;
    }

    setIsManualEditSaving(true);

    try {
      await saveExperienceInventoryFromModified(
        manualEditValidationPayload.modifiedValues
      );
      setManualEditValidationPayload(null);
    } finally {
      setIsManualEditSaving(false);
    }
  };

  const handleFieldsChange = useCallback(
    (changedFields: FieldData[]) => {
      if (!rawInventory || !dependencies) {
        return;
      }

      const retrieved = retrieveDataFromNamePath(changedFields[0]?.name);

      if (!retrieved) {
        return;
      }

      const { dateKey, itemId } = retrieved;

      const existing =
        modifiedInventory?.[dateKey]?.experiences?.[Number(itemId)];

      if (!existing) {
        return;
      }

      if (!isEnabledByMessage(existing.message)) {
        return;
      }

      let currentInventory = modifiedInventory ?? {};

      const prevEntry = currentInventory[dateKey]?.experiences?.[
        Number(itemId)
      ] as Record<string, unknown> | undefined;

      currentInventory = updateExperienceStockForDate(
        currentInventory ?? {},
        Number(itemId),
        dateKey,
        changedFields[0]?.value,
        dependencies
      );

      const newEntry = currentInventory[dateKey]?.experiences?.[
        Number(itemId)
      ] as Record<string, unknown> | undefined;

      const formFields = getChangedFormFields(
        prevEntry,
        newEntry,
        dateKey,
        itemId
      );

      setModifiedInventory(currentInventory);

      if (formFields.length > 0) {
        form?.setFields(formFields);
      }
    },
    [rawInventory, dependencies, modifiedInventory, setModifiedInventory, form]
  );

  if (isFetching && !!sortedExperiences) {
    return <ExperienceTabSkeleton />;
  }

  return (
    <Form
      form={form}
      className={classNames(styles.experiencesInventoryForm, {
        [styles.noPadding]:
          [...sortedExperiences, ...sortedExperiencesIsAlwaysIncludedWithNight]
            .length === 0,
      })}
      layout="vertical"
      onFinish={onFinish}
      initialValues={inventoryExperiences}
      onFieldsChange={handleFieldsChange}
    >
      {sortedExperiences.map((experience) => {
        const categoryName = experiencesCategories?.find(
          (cat) => cat.id === experience.categoryId
        )?.name;

        return (
          <ExperienceTableItem
            key={experience.id}
            experience={experience}
            categoryName={categoryName}
          />
        );
      })}

      {sortedExperiencesIsAlwaysIncludedWithNight.length > 0 && (
        <Divider className={styles.experienceDivider} type="horizontal">
          Areas and facilities
        </Divider>
      )}

      {sortedExperiencesIsAlwaysIncludedWithNight.map((experience) => {
        const categoryName = experiencesCategories?.find(
          (cat) => cat.id === experience.categoryId
        )?.name;

        return (
          <ExperienceTableItem
            key={experience.id}
            experience={experience}
            categoryName={categoryName}
          />
        );
      })}

      {sortedExperiences.length === 0 &&
        sortedExperiencesIsAlwaysIncludedWithNight.length === 0 && (
          <EmptyState title="Create new activities" />
        )}

      <ExperienceConfigurationDrawer />
      <TimeslotsModal />
      <ValidationExperienceManualEditModal
        isOpened={!!manualEditValidationPayload}
        experienceNames={manualEditValidationPayload?.experienceNames ?? []}
        packNames={manualEditValidationPayload?.packNames ?? []}
        isLoading={isManualEditSaving}
        handleCancel={handleCancelManualEditValidation}
        handleSaveChange={handleSaveManualEditValidation}
      />
    </Form>
  );
};
